import { Check, Sparkles, MessageCircle, FileText, Eye, Hammer } from 'lucide-react';
import type { GuidedPhase, BuildPlan } from '../../types/guided-builder';
import { phaseToWizardStep, WIZARD_STEPS } from '../../types/guided-builder';

const STEP_ICONS: React.ElementType[] = [Sparkles, MessageCircle, FileText, Eye, Hammer];

interface GuidedStepBarProps {
  currentPhase: GuidedPhase;
  buildPlan?: BuildPlan | null;
  /** Currently viewed step (for backward navigation). null = current step. */
  viewingStep?: number | null;
  onStepClick?: (stepIndex: number) => void;
}

/**
 * @deprecated Use GuidedStepBar instead. This re-export exists for backward compatibility.
 */
export const GuidedPhaseBar = GuidedStepBar;

export function GuidedStepBar({
  currentPhase,
  buildPlan,
  viewingStep,
  onStepClick,
}: GuidedStepBarProps) {
  const currentStepIndex = phaseToWizardStep(currentPhase);

  // Sub-progress for Build step
  const stepProgress =
    currentStepIndex === 4 && buildPlan?.approved
      ? `Step ${(buildPlan.currentStepIndex ?? 0) + 1} of ${buildPlan.steps.length}`
      : null;

  return (
    <div className="flex items-center gap-1 px-4 py-2 border-b bg-muted/30 min-h-[40px]">
      {WIZARD_STEPS.map((step) => {
        const isCompleted = step.index < currentStepIndex;
        const isActive = step.index === currentStepIndex;
        const isViewing = viewingStep === step.index;
        const Icon = STEP_ICONS[step.index];
        const isClickable = isCompleted && onStepClick;

        return (
          <div key={step.index} className="flex items-center gap-1">
            {step.index > 0 && (
              <div
                className={`h-px w-3 sm:w-6 ${step.index <= currentStepIndex ? 'bg-primary' : 'bg-border'}`}
              />
            )}
            <button
              type="button"
              disabled={!isClickable}
              onClick={() => isClickable && onStepClick(step.index)}
              className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs transition-colors ${
                isViewing
                  ? 'bg-primary/20 text-primary font-medium ring-1 ring-primary/40'
                  : isActive
                    ? 'bg-primary text-primary-foreground font-medium'
                    : isCompleted
                      ? 'text-primary cursor-pointer hover:bg-primary/10'
                      : 'text-muted-foreground'
              } ${!isClickable ? 'cursor-default' : ''}`}
            >
              {isCompleted ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
              <span className="hidden sm:inline">{step.label}</span>
            </button>
          </div>
        );
      })}

      {/* Step sub-progress during Build step */}
      {currentStepIndex === 4 && stepProgress && (
        <span className="ml-auto text-xs text-muted-foreground">{stepProgress}</span>
      )}
    </div>
  );
}
